import { RootState } from "core-integration/src/store";
import { IGroupFixture } from "core-integration/src/store/fixtures/types";
import { getTeamsById } from "core-integration/src/store/teams/reducers";
import { ITeamsById } from "core-integration/src/store/teams/types";
import { formatRawAsISO } from "core-integration/src/utils/datetime";
import { formatInTimeZone } from "date-fns-tz";
import * as React from "react";
import { connect } from "react-redux";
import styled, { css } from "styled-components";
import Badge from "./Badge";

const StyledFixture = styled.div<IStyledFixtureProps>`
  overflow: auto;
  border-radius: ${({ theme }) => theme.radii[1]};
  padding: 1px 0;
  background-color: ${({ theme }) => theme.colors.white};
  color: ${({ theme }) => theme.colors.primary};

  ${(props) =>
    props.isOpen &&
    css`
      padding: 0px;
      border-bottom-right-radius: 0;
      border-bottom-left-radius: 0;
      background-color: ${({ theme }) => theme.colors.secondaryTwo};
    `}

  @media (min-width: ${({ theme }) => theme.breakpoints[1]}) {
    position: relative;
  }
`;

const Teams = styled.div`
  display: flex;
  margin: 0.7rem 0;
  align-items: center;
`;

const Team = styled.div`
  flex: 1 1 calc(50% - (6.6rem / 2));
  text-align: start;
`;

const TeamH = styled(Team)`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  text-align: end;
`;

const TeamA = styled(Team)`
  display: flex;
  align-items: center;
  justify-content: flex-start;
`;

const TeamName = styled.abbr`
  vertical-align: middle;
  font-family: ${({ theme }) => theme.fonts.impact};
  font-size: ${({ theme }) => theme.fontSizes[6]};
  font-weight: normal;
  text-decoration: none;

  @media (min-width: ${({ theme }) => theme.breakpoints[2]}) {
    font-size: ${({ theme }) => theme.fontSizes[7]};

    &:after {
      content: attr(title);
    }
    span {
      display: none;
    }
  }
`;

const FixtureBadge = styled.div`
  display: inline-block;
  margin-inline-end: 0.6rem;
  margin-inline-start: 0.6rem;
  width: 40px;
  height: 40px;

  @media (min-width: ${({ theme }) => theme.breakpoints[4]}) {
    margin-inline-end: 0.8rem;
    margin-inline-start: 0.8rem;
  }
`;

const FixtureKOTime = styled.time`
  flex: 1 0 6.6rem;
  display: inline-block;
  margin: 0 ${({ theme }) => `${theme.space[2]}`};
  padding: ${({ theme }) => `0.8rem ${theme.space[2]} ${theme.space[2]}`};
  border: ${({ theme }) => theme.borders[0]};
  border-radius: ${({ theme }) => theme.radii[1]};
  background-color: ${({ theme }) => theme.colors.white};
  font-family: ${({ theme }) => theme.fonts.impact};
  font-size: ${({ theme }) => theme.fontSizes[5]};
  font-weight: normal;
  color: ${({ theme }) => theme.colors.primary};
  text-align: center;

  @media (min-width: ${({ theme }) => theme.breakpoints[1]}) {
    font-size: ${({ theme }) => theme.fontSizes[6]};
  }
`;

const FixtureScore = styled.div`
  flex: 1 0 6.6rem;
  display: flex;
  align-items: center;
  margin: 0 ${({ theme }) => `${theme.space[2]}`};
  padding: ${({ theme }) => `0.8rem ${theme.space[1]} ${theme.space[2]}`};
  border: ${({ theme }) => theme.borders[3]};
  border-radius: ${({ theme }) => theme.radii[1]};
  background-color: ${({ theme }) => theme.colors.primary};
`;

const FixtureScoreTeam = styled.span`
  flex: 1;
  font-family: ${({ theme }) => theme.fonts.impact};
  font-size: ${({ theme }) => theme.fontSizes[5]};
  font-weight: normal;
  text-align: center;
  color: ${({ theme }) => theme.colors.white};

  @media (min-width: ${({ theme }) => theme.breakpoints[1]}) {
    font-size: ${({ theme }) => theme.fontSizes[6]};
  }
`;

const Hyphen = styled.span`
  font-family: ${({ theme }) => theme.fonts.impact};
  color: ${({ theme }) => theme.colors.white};
`;

interface IPropsFromState {
  teamsById: ITeamsById;
}

interface IOwnProps {
  fixture: IGroupFixture;
}

interface IStyledFixtureProps {
  isOpen?: boolean;
}

type Props = IPropsFromState & IOwnProps & IStyledFixtureProps;

const Fixture: React.FC<Props> = ({ fixture, teamsById, isOpen }) => {
  return (
    <StyledFixture isOpen={isOpen}>
      <Teams>
        <TeamH>
          <TeamName title={teamsById[fixture.team_h].name}>
            <span>{teamsById[fixture.team_h].short_name}</span>
          </TeamName>
          <FixtureBadge>
            <Badge
              team={teamsById[fixture.team_h]}
              isPresentation={true}
              sizes="(min-width: 1024px) 40px, 30px"
            />
          </FixtureBadge>
        </TeamH>
        {fixture.started && fixture.team_h_score !== null ? (
          <FixtureScore>
            <FixtureScoreTeam>{fixture.team_h_score}</FixtureScoreTeam>
            <Hyphen>-</Hyphen>
            <FixtureScoreTeam>{fixture.team_a_score}</FixtureScoreTeam>
          </FixtureScore>
        ) : fixture.kickoff_time ? (
          <FixtureKOTime dateTime={formatRawAsISO(fixture.kickoff_time)}>
            {formatInTimeZone(
              fixture.kickoffDate,
              Intl.DateTimeFormat().resolvedOptions().timeZone,
              "HH:mm",
            )}
          </FixtureKOTime>
        ) : (
          <FixtureKOTime as="span">-</FixtureKOTime>
        )}
        <TeamA>
          <FixtureBadge>
            <Badge
              team={teamsById[fixture.team_a]}
              isPresentation={true}
              sizes="(min-width: 1024px) 40px, 30px"
            />
          </FixtureBadge>
          <TeamName title={teamsById[fixture.team_a].name}>
            <span>{teamsById[fixture.team_a].short_name}</span>
          </TeamName>
        </TeamA>
      </Teams>
    </StyledFixture>
  );
};

const mapStateToProps = (state: RootState): IPropsFromState => ({
  teamsById: getTeamsById(state),
});

export default connect(mapStateToProps)(Fixture);
